<template>
  <transition name="cookie-modal-fade">
    <div v-if="showModal" class="cookie-modal-overlay" @click.self="close">
      <section class="cookie-modal" role="dialog" aria-modal="true" aria-label="Cookie preferences">
        <div class="cookie-modal__body-top">
          <button type="button" class="cookie-modal__close" @click="close" aria-label="Close">
            ×
          </button>
        </div>

        <p class="cookie-modal__intro">
          Izaberite koje opcione kategorije kolačića dozvoljavate. Neophodni kolačići su uvek uključeni.
        </p>

        <div class="cookie-modal__categories">
          <div class="cookie-category">
            <div class="cookie-category__content">
              <strong>Neophodni</strong>
              <p>Potrebni za osnovne funkcije sajta, tok rezervacije i bezbedna plaćanja.</p>
            </div>
            <label class="switch">
              <input type="checkbox" checked disabled />
              <span class="slider"></span>
            </label>
          </div>

          <div class="cookie-category">
            <div class="cookie-category__content">
              <strong>Analitika</strong>
              <p>Pomažu nam da razumemo saobraćaj i unapredimo performanse (npr. Google Analytics).</p>
            </div>
            <label class="switch">
              <input v-model="draft.analytics" type="checkbox" />
              <span class="slider"></span>
            </label>
          </div>

          <div class="cookie-category">
            <div class="cookie-category__content">
              <strong>Funkcionalni</strong>
              <p>Pamte podešavanja poput jezika i drugih unapređenih korisničkih opcija.</p>
            </div>
            <label class="switch">
              <input v-model="draft.functional" type="checkbox" />
              <span class="slider"></span>
            </label>
          </div>

          <div class="cookie-category">
            <div class="cookie-category__content">
              <strong>Treće strane</strong>
              <p>Opcioni sadržaji i integracije trećih strana koji nisu neophodni za rezervaciju/plaćanje.</p>
            </div>
            <label class="switch">
              <input v-model="draft.thirdParty" type="checkbox" />
              <span class="slider"></span>
            </label>
          </div>
        </div>

        <div class="cookie-modal__actions">
          <button type="button" class="cookie-btn cookie-btn--outline" @click="rejectNonEssential">
            Odbij opcione
          </button>
          <button type="button" class="cookie-btn cookie-btn--primary" @click="acceptAll">
            Prihvati sve
          </button>
          <button type="button" class="cookie-btn cookie-btn--ghost" @click="save">
            Sačuvaj podešavanja
          </button>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
import {
  acceptAllCookies,
  closeCookiePreferences,
  cookieConsentState,
  rejectNonEssentialCookies,
  saveCookiePreferences,
} from '@/utils/cookieConsent'

export default {
  name: 'CookiePreferencesModal',
  data() {
    return {
      consentState: cookieConsentState,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : Number.MAX_SAFE_INTEGER,
      draft: {
        analytics: false,
        functional: false,
        thirdParty: false,
      },
    }
  },
  computed: {
    showModal() {
      return this.consentState.showPreferences
    },
    isMobile() {
      return this.viewportWidth < 992
    },
  },
  watch: {
    showModal: {
      immediate: true,
      handler(nextValue) {
        if (nextValue) this.resetDraft()
        this.updatePageState()
      },
    },
  },
  mounted() {
    if (typeof window !== 'undefined') {
      this.viewportWidth = window.innerWidth
      window.addEventListener('resize', this.handleResize, { passive: true })
    }
  },
  beforeDestroy() {
    this.consentState.showPreferences = false
    this.updatePageState()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize)
    }
  },
  methods: {
    resetDraft() {
      const consent = this.consentState.consent
      this.draft.analytics = Boolean(consent.analytics)
      this.draft.functional = Boolean(consent.functional)
      this.draft.thirdParty = Boolean(consent.thirdParty)
    },
    close() {
      closeCookiePreferences()
    },
    acceptAll() {
      acceptAllCookies()
    },
    rejectNonEssential() {
      rejectNonEssentialCookies()
    },
    save() {
      saveCookiePreferences(this.draft)
    },
    handleResize() {
      this.viewportWidth = typeof window !== 'undefined' ? window.innerWidth : Number.MAX_SAFE_INTEGER
      this.updatePageState()
    },
    updatePageState() {
      if (typeof document === 'undefined' || !document.body || !document.documentElement) return

      const isOpen = this.showModal
      const shouldApplyMobileOverlayClass = isOpen && this.isMobile
      const overflowValue = isOpen ? 'hidden' : ''
      const overscrollValue = isOpen ? 'none' : ''

      document.body.style.overflowY = overflowValue
      document.body.style.overflow = overflowValue
      document.body.style.overscrollBehaviorY = overscrollValue
      document.documentElement.style.overflowY = overflowValue
      document.documentElement.style.overflow = overflowValue
      document.documentElement.style.overscrollBehaviorY = overscrollValue
      document.body.classList.toggle('ota-date-overlay-open', shouldApplyMobileOverlayClass)
    },
  },
}
</script>

<style lang="scss" scoped>
.cookie-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 100001;
  background: rgba(5, 16, 54, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}

.cookie-modal {
  width: 100%;
  max-width: 680px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #dde3ea;
  box-shadow: 0 14px 36px rgba(5, 16, 54, 0.25);
  padding: 18px;

  &__body-top {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 2px;
  }

  &__intro {
    margin: 12px 0 16px;
    color: #5a6780;
    font-size: 14px;
    line-height: 1.5;
  }

  &__categories {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__actions {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
    background: #ffffff;
    border-top: 1px solid #e3e8ef;
    padding-top: 14px;
    padding-bottom: max(2px, env(safe-area-inset-bottom, 0px));
  }

  &__close {
    border: 1px solid #d6deea;
    background: #ffffff;
    cursor: pointer;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    font-size: 22px;
    line-height: 1;
    color: #5a6780;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
}

.cookie-category {
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  &__content {
    flex: 1;
    min-width: 0;

    strong {
      display: block;
      margin-bottom: 2px;
      color: #051036;
      font-size: 15px;
    }

    p {
      margin: 0;
      color: #697488;
      font-size: 13px;
      line-height: 1.4;
    }
  }
}

.switch {
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  width: 46px;
  height: 26px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #d4dbe6;
  transition: 0.2s;
  border-radius: 30px;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 3px;
    top: 3px;
    background-color: #ffffff;
    transition: 0.2s;
    border-radius: 50%;
  }
}

input:checked + .slider {
  background-color: #3554d1;
}

input:checked + .slider:before {
  transform: translateX(20px);
}

input:disabled + .slider {
  background-color: #8ea0c8;
  cursor: not-allowed;
}

.cookie-btn {
  border: 1px solid transparent;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &--outline {
    background: #ffffff;
    color: #1f2937;
    border-color: #b8c3d1;
  }

  &--ghost {
    background: #f7f8fa;
    color: #1f2937;
    border-color: #dde3ea;
  }

  &--primary {
    background: #3554d1;
    color: #ffffff;
  }
}

.cookie-modal-fade-enter-active,
.cookie-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.cookie-modal-fade-enter,
.cookie-modal-fade-leave-to {
  opacity: 0;
}

@supports (height: 100dvh) {
  .cookie-modal {
    max-height: calc(100dvh - 32px);
  }
}

@media (max-width: 767px) {
  .cookie-modal-overlay {
    align-items: flex-start;
    padding: max(10px, env(safe-area-inset-top, 0px)) 10px
      calc(10px + env(safe-area-inset-bottom, 0px));
  }

  .cookie-modal {
    max-height: calc(100vh - 20px);
    padding: 14px;
    border-radius: 12px;

    &__intro {
      margin-top: 8px;
      margin-bottom: 12px;
      font-size: 13px;
    }

    &__actions {
      justify-content: stretch;
    }
  }

  .cookie-category {
    padding: 10px;
    align-items: flex-start;
  }

  .cookie-btn {
    flex: 1 1 calc(50% - 4px);
  }
}

@supports (height: 100dvh) {
  @media (max-width: 767px) {
    .cookie-modal {
      max-height: calc(100dvh - 20px);
    }
  }
}
</style>
